<script setup lang="ts">
import avatarMap from '@/assets/avatars/avatarsMap';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useAppApi } from '@/plugins/appApi';
import { useProjectStore } from '@/stores/project';
import useLoading from '@/composables/useLoading';

const projectStore = useProjectStore();

const props = defineProps<{
  phone: string;
  name: string;
  role: string;
  email: string;
  img?: string;
  id?: number;
}>();

const avatarUrl = computed(() => {
  if (imageBase64.value) {
    return 'data:image/png;base64, ' + imageBase64.value;
  }

  if (props.img) {
    return avatarMap[props.img + '.png'];
  }

  return '';
});

const imageBase64: Ref<string | undefined> = ref(undefined);

const loadingAvatar = ref(false);

const client = useAppApi();
onMounted(() =>
  useLoading(loadingAvatar, async () => {
    if (props.id && props.id > -1) {
      const result = await client.GET('/api/projects/{uid}/contacts/{contactId}/photo', {
        params: {
          path: {
            uid: projectStore.getProjectUid(),
            contactId: props.id
          }
        }
      });

      imageBase64.value = result.data;
    }
  })
);

const openEmail = () => {
  const el = document.createElement('a');
  el.href = 'mailto:' + props.email;
  el.click();
};

const phoneCall = () => {
  const el = document.createElement('a');
  el.href = 'tel:' + props.phone;
  el.click();
};
</script>

<template>
  <div>
    <div class="min-[1300px]:hidden flex items-stretch gap-4 p-2 bg-lumar-gray-10 rounded-lg">
      <Skeleton
        height="48px"
        width="48px"
        class="mb-2"
        border-radius="500px"
        style="margin-bottom: 0"
        v-if="loadingAvatar"
        :pt="{
          root: {
            style: {
              background: '#dee9fc'
            }
          }
        }"
      ></Skeleton>
      <Avatar v-if="avatarUrl && !loadingAvatar" :image="avatarUrl" shape="circle" size="large" />
      <Avatar
        :label="name.charAt(0)"
        style="background-color: #dee9fc; color: #1a2551"
        v-if="!avatarUrl && !loadingAvatar"
        shape="circle"
        size="large"
      />
      <div class="flex flex-col justify-between flex-grow">
        <span class="text-xs text-lumar-dark-gray">{{ role }}</span>
        <span class="text-lumar-gray font-semibold">{{ name }}</span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          class="text-lumar-blue"
          icon="pi pi-phone"
          variant="text"
          rounded
          aria-label="phone"
          @click="phoneCall"
        />
        <Button
          class="text-lumar-blue"
          icon="pi pi-envelope"
          variant="text"
          rounded
          aria-label="email"
          @click="openEmail"
        />
      </div>
    </div>
    <div class="hidden min-[1300px]:flex flex-col gap-8 p-6 bg-lumar-gray-10 rounded-lg">
      <Skeleton
        height="48px"
        width="48px"
        class="mb-2"
        border-radius="500px"
        style="margin-bottom: 0"
        v-if="loadingAvatar"
        :pt="{
          root: {
            style: {
              background: '#dee9fc'
            }
          }
        }"
      ></Skeleton>
      <Avatar v-if="avatarUrl && !loadingAvatar" :image="avatarUrl" shape="circle" size="large" />
      <Avatar
        :label="name.charAt(0)"
        style="background-color: #dee9fc; color: #1a2551"
        v-if="!avatarUrl && !loadingAvatar"
        shape="circle"
        size="large"
      />
      <div class="grid grid-rows-4">
        <div class="truncate">
          <span class="text-base text-lumar-dark-gray">{{ role }}</span>
        </div>
        <div class="truncate">
          <span class="text-[24px] leading-[1.2] text-lumar-gray font-semibold">{{ name }}</span>
        </div>
        <div>
          <Button
            class="text-lumar-blue"
            icon="pi pi-phone"
            variant="text"
            rounded
            aria-label="phone"
            @click="phoneCall"
          />
          <div class="inline-block align-text-bottom text-base">
            <span>{{ phone }}</span>
          </div>
        </div>
        <div>
          <Button
            class="text-lumar-blue"
            icon="pi pi-envelope"
            variant="text"
            rounded
            aria-label="email"
            @click="openEmail"
          />
          <div class="inline-block align-text-bottom text-sm">
            <span>{{ email }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
